import type { BankDraftQuestion } from 'modules/learnArtifacts';

const MIN_CHOICE_OPTIONS = 2;
const BLANK_OPTION_COUNT = 4;

/** Mirrors the API's `hasQuestionContent`: text, an image or a math node counts; stray markup does not. */
export function hasContent(html: string | undefined): boolean {
  if (!html || !html.trim()) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if ((doc.body.textContent ?? '').replace(/[\s\u200B-\u200D\uFEFF]/g, '')) return true;
  const hasImage = Array.from(doc.querySelectorAll('img')).some(
    (img) => img.getAttribute('src')?.trim() || img.getAttribute('data-b2-key')?.trim(),
  );
  const hasMath = Array.from(doc.querySelectorAll('span.math-tex[data-latex]')).some((node) =>
    node.getAttribute('data-latex')?.trim(),
  );
  return hasImage || hasMath;
}

/**
 * A question the API would refuse — it has to be fixed or left out before Add
 * to bank. Mirrors `getChoiceQuestionIssues` in the API for the choice types,
 * which is strict: every option needs content and the key must name an option.
 */
export type DraftIssue = 'prompt' | 'options' | 'answer';

const KEYED_TYPES = new Set(['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE']);

function computeIssue(question: BankDraftQuestion): DraftIssue | null {
  if (!hasContent(question.prompt)) return 'prompt';
  if (!KEYED_TYPES.has(question.type)) return null;

  const options = question.options ?? [];
  if (options.length < MIN_CHOICE_OPTIONS || !options.every((option) => hasContent(option.value))) {
    return 'options';
  }
  const ids = new Set(options.map((option) => option.id));
  const key = question.correctAnswer;
  const keyed = Array.isArray(key)
    ? key.length > 0 && key.every((id) => ids.has(id))
    : typeof key === 'string' && ids.has(key);
  return keyed ? null : 'answer';
}

// Drafts are replaced, never mutated, so a row's verdict is good until its
// object is. Editing one question then re-parses that question, not the batch.
const issueCache = new WeakMap<BankDraftQuestion, DraftIssue | null>();

export function draftIssue(question: BankDraftQuestion): DraftIssue | null {
  if (!issueCache.has(question)) issueCache.set(question, computeIssue(question));
  return issueCache.get(question) ?? null;
}

/** An empty multiple-choice question, ready to be filled in. */
export function createBlankQuestion(id: string): BankDraftQuestion {
  return {
    id,
    type: 'MULTIPLE_CHOICE',
    prompt: '',
    options: Array.from({ length: BLANK_OPTION_COUNT }, (_, index) => ({
      id: `opt_${index}`,
      value: '',
    })),
    correctAnswer: '',
    points: 1,
  };
}
